import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { t } from '../../i18n';
import { useSheetSwipe } from '../useSheetSwipe';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Open dialogs, so the page behind stays still while any is open. */
let open = 0;
function lockPage(): () => void {
  open += 1;
  if (open === 1) document.documentElement.classList.add('modal-open');
  return () => {
    open -= 1;
    if (open === 0) document.documentElement.classList.remove('modal-open');
  };
}

/**
 * Accessible dialog: focus moves in, Tab stays inside, Escape closes when allowed, focus returns
 * on close. The title and footer stay put while the body scrolls; on phones it rises from the
 * bottom of the screen. A dialog that can be closed has a close button in its title bar, and on
 * phones a grabber: it can be pulled down to close (src/ui/useSheetSwipe.ts).
 */
export function Modal({
  title,
  children,
  onClose,
  footer,
  wide = false,
  className = '',
}: {
  title: string;
  children: ReactNode;
  /** When set, Escape, the backdrop and the close button close the dialog. */
  onClose?: () => void;
  footer?: ReactNode;
  wide?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // A body that scrolls can be focused, so it can be scrolled with the keyboard.
  const [scrolls, setScrolls] = useState(false);
  useEffect(() => {
    const body = bodyRef.current;
    if (!body || typeof ResizeObserver === 'undefined') return;
    const check = () => setScrolls(body.scrollHeight > body.clientHeight + 1);
    const observer = new ResizeObserver(check);
    observer.observe(body);
    for (const child of body.children) observer.observe(child);
    check();
    return () => observer.disconnect();
  }, []);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useSheetSwipe(ref, onClose);

  useEffect(() => {
    const unlock = lockPage();
    const previous = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const first =
      node?.querySelector<HTMLElement>('[data-autofocus]') ??
      node?.querySelector<HTMLElement>(
        `.modal-body ${FOCUSABLE.split(', ').join(', .modal-body ')}`,
      ) ??
      node?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? node)?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (!node) return;
      if (e.key === 'Escape' && onCloseRef.current) {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (items.length === 0) return;
      const firstItem = items[0] as HTMLElement;
      const lastItem = items[items.length - 1] as HTMLElement;
      if (e.shiftKey && document.activeElement === firstItem) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && document.activeElement === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      unlock();
      if (previous && document.contains(previous)) previous.focus({ preventScroll: true });
    };
  }, []);

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div
        ref={ref}
        className={`modal ${wide ? 'modal-wide' : ''} ${onClose ? 'modal-closable' : ''} ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className="modal-head">
          <h2 id={titleId} className="modal-title">
            {title}
          </h2>
          {onClose && (
            <button
              type="button"
              className="icon-btn modal-close"
              aria-label={t('action.close')}
              onClick={onClose}
            >
              <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                <path d="M5 5l10 10M15 5 5 15" />
              </svg>
            </button>
          )}
        </header>
        <div
          ref={bodyRef}
          className="modal-body"
          tabIndex={scrolls ? 0 : undefined}
          aria-label={scrolls ? title : undefined}
          role={scrolls ? 'region' : undefined}
        >
          {children}
        </div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}
